import { useEffect, useMemo, useState } from 'react';
import { getGeoQueries, unwrapGeoPayload } from '../../../api';
import { compareGeoQueryIds } from '../utils/geoPresentation';

const EMPTY_PAGE = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 25,
  totalPages: 0,
};

/** Paginated GEO prompts — filters and paging are applied on the backend. */
export default function useGeoQueries({ promptsParams = {}, enabled = true, refreshKey = 0 } = {}) {
  const [data, setData] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const paramsKey = useMemo(() => JSON.stringify(promptsParams || {}), [promptsParams]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await getGeoQueries(promptsParams);
        if (!cancelled) {
          const payload = unwrapGeoPayload(response) || EMPTY_PAGE;
          const items = Array.isArray(payload.items)
            ? [...payload.items].sort((a, b) => compareGeoQueryIds(a.id, b.id))
            : [];
          setData({
            items,
            total: Number(payload.total) || 0,
            page: Number(payload.page) || 1,
            pageSize: Number(payload.pageSize) || 25,
            totalPages: Number(payload.totalPages) || 0,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load prompts');
          setData(EMPTY_PAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const timer = window.setTimeout(load, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [paramsKey, enabled, promptsParams, refreshKey]);

  return {
    queries: data.items,
    total: data.total,
    page: data.page,
    pageSize: data.pageSize,
    totalPages: data.totalPages,
    loading,
    error,
  };
}
